import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {ReactNode} from 'react';

import {assert} from 'Asserts';
import BenchlingConfig from 'BenchlingConfig';
import {Audience} from 'graphql-client/__generated/graphqlGlobalTypes';
import notifications from 'notifications';
import {GRAY_DARKER} from 'ui-library/Colors';
import {Header3} from 'ui-library/Headers';
import SettingsField from 'ui-library/SettingsField';

import * as AppDefinitionActions from '../AppDefinitionActions';
import * as BenchlingAppActions from '../BenchlingAppActions';
import {UpdateBenchlingAppDefinitionArgs} from '../BenchlingAppDAO';
import {BenchlingAppCredentials} from './BenchlingAppCredentials';
import {AppDefinition} from './BenchlingAppsPage';
import {AppDefinitionWithOAuth} from './BenchlingGlobalAppPage';
import PublicKeySection from './PublicKeySection';
import WebhookSetupSection from './WebhookSetupSection';
import WebhookURLSection from './WebhookURLSection';

export default function BenchlingAppOverview({
  appDefinition,
  refetchAppDefinition,
}: {
  appDefinition: AppDefinitionWithOAuth;
  refetchAppDefinition: () => void;
}): JSX.Element {
  async function handleSubmitDefinition(newValue: UpdateBenchlingAppDefinitionArgs): Promise<void> {
    if (BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION) {
      await AppDefinitionActions.updateAppDefinition(appDefinition.id, newValue);
    } else {
      assert(
        appDefinition.installation != null,
        'Expected app definition to have an associated installation'
      );
      await BenchlingAppActions.deprecatedUpdateDefinition(appDefinition.installation.id, newValue);
    }
    refetchAppDefinition();
    notifications.success(<>Successfully updated app</>);
  }

  return (
    <OverviewSection>
      <BenchlingAppGlobalInformation
        appDefinition={appDefinition}
        refetchAppDefinition={refetchAppDefinition}
        handleSubmitDefinition={handleSubmitDefinition}
      />
      {BenchlingConfig.ENABLE_BENCHLING_APPS_CREATE_INSTALL_SEPARATION ? (
        <></>
      ) : (
        <BenchlingAppInstallInformation appDefinition={appDefinition} />
      )}
      <BenchlingAppCredentials
        appDefinition={appDefinition}
        refetchAppDefinition={refetchAppDefinition}
        handleSubmitDefinition={handleSubmitDefinition}
      />
      <BenchlingAppDistributionSettings appDefinition={appDefinition} />
    </OverviewSection>
  );
}

export function BenchlingAppGlobalInformation({
  appDefinition,
  refetchAppDefinition,
  handleSubmitDefinition,
}: {
  appDefinition: AppDefinition;
  refetchAppDefinition: () => void;
  handleSubmitDefinition: (newValue: UpdateBenchlingAppDefinitionArgs) => void;
}): JSX.Element {
  let creatorName = '';
  if (appDefinition.creator != null) {
    creatorName = appDefinition.creator.name;
  }
  const webhookSection = BenchlingConfig.ENABLE_BENCHLING_APPS_WEBHOOK_HEADERS ? (
    <WebhookSetupSection
      appDefinition={appDefinition}
      onUpdateWebhookSetup={async (
        newWebhookUrl: string | null,
        newWebhookHeaders: Array<{key: string; value: string}>
      ) => {
        await AppDefinitionActions.updateAppDefinition(appDefinition.id, {
          webhookUrl: newWebhookUrl || null,
          webhookHeaders: newWebhookHeaders || null,
        });
      }}
    />
  ) : (
    <WebhookURLSection
      webhookUrl={appDefinition.webhookUrl}
      onUpdateWebhookUrl={(newWebhookUrl: string) => {
        handleSubmitDefinition({webhookUrl: newWebhookUrl || null});
      }}
    />
  );
  const publicKeySection = (
    <PublicKeySection
      value={appDefinition.publicKey}
      onChangePublicKey={(newPublicKey: string) => {
        handleSubmitDefinition({publicKey: newPublicKey || null});
      }}
    />
  );
  const thirdItem = BenchlingConfig.ENABLE_BENCHLING_APPS_WEBHOOK_HEADERS ? publicKeySection : webhookSection;
  const fourthItem = BenchlingConfig.ENABLE_BENCHLING_APPS_WEBHOOK_HEADERS
    ? webhookSection
    : publicKeySection;
  return (
    <div>
      <Header3>Global Information</Header3>
      <div className={css(styles.subheader)}>
        Information about your app that is globally used for any version of this app.
      </div>

      <BenchlingAppSettingsRow>
        <SettingsField
          label="App ID"
          name="appId"
          value={appDefinition.id}
          isBeingCreated={false}
          onChange={() => {}}
          isWritable={false}
          dataTestAttrs={{component: 'BenchlingAppOverview', element: 'appDefinitionId'}}
        />
        <SettingsField
          label="Creator"
          name="creator"
          value={creatorName}
          isBeingCreated={false}
          onChange={() => {}}
          isWritable={false}
        />
        {thirdItem}
      </BenchlingAppSettingsRow>
      <BenchlingAppSettingsRow>{fourthItem}</BenchlingAppSettingsRow>
    </div>
  );
}

export function BenchlingAppInstallInformation({appDefinition}: {appDefinition: AppDefinition}): JSX.Element {
  return (
    <div>
      <Header3 className={css(styles.sectionHead)}>Install Information</Header3>
      <BenchlingAppSettingsRow>
        <SettingsField
          label="Install ID"
          name="installId"
          value={appDefinition.installation ? appDefinition.installation.id : ''}
          isBeingCreated={false}
          onChange={() => {}}
          isWritable={false}
        />
      </BenchlingAppSettingsRow>
    </div>
  );
}

export function BenchlingAppDistributionSettings({
  appDefinition,
}: {
  appDefinition: AppDefinition;
}): JSX.Element {
  return (
    <div>
      <Header3 className={css(styles.sectionHead)}>Distribution Settings</Header3>
      <div className={css(styles.subheader)}>Manage how your app can be installed and used by consumers.</div>
      <BenchlingAppSettingsRow>
        <SettingsField
          label="Audience"
          name="audience"
          value={appDefinition.audience === Audience.PUBLIC ? 'Public' : 'Private'}
          isBeingCreated={false}
          onChange={() => {}}
          isWritable={false}
        />
      </BenchlingAppSettingsRow>
    </div>
  );
}

interface BenchlingAppSettingsRowProps {
  className?: string;
  children: ReactNode;
}

export function BenchlingAppSettingsRow(props: BenchlingAppSettingsRowProps): JSX.Element {
  return (
    <div className={classNames(props.className, css(styles.settingsRow))}>
      {React.Children.map(props.children, (child) => (
        <div className={css(styles.settingsColumn)}>
          <div className={classNames(css(styles.settingsField), 'u-base-text')}>{child}</div>
        </div>
      ))}
    </div>
  );
}

interface OverviewSectionProps {
  children: ReactNode;
}

export function OverviewSection(props: OverviewSectionProps): JSX.Element {
  return (
    <div>
      {React.Children.map(props.children, (child) => (
        <div className={css(styles.overviewSection)}>{child}</div>
      ))}
    </div>
  );
}

const styles = StyleSheet.create({
  settingsRow: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'flex-start',
    width: '100%',
  },
  settingsColumn: {
    flex: 0.33,
    minWidth: 0,
  },
  settingsField: {
    width: '80%',
    marginBottom: '0',
  },
  sectionHead: {
    marginTop: '16px',
  },
  missing: {
    color: GRAY_DARKER,
  },
  benchlingAppMetadataField: {
    marginBottom: 15,
  },
  subheader: {
    marginBottom: '7px',
  },
  overviewSection: {
    marginBottom: '24px',
  },
});
